import React from 'react';
import { Link } from 'react-router-dom';
import Spline from '@splinetool/react-spline';
import { ArrowRight, Server, Shield, Zap, Globe } from 'lucide-react';
import { heroContent, features } from '../data/mock';

export const HomePage = () => {
  return (
    <div className="grid-pattern bg-[var(--bg-primary)] min-h-screen">
      {/* Hero Section */}
      <section className="min-h-screen flex items-center pt-16 md:pt-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center py-8 md:py-12">
          {/* Left Content */}
          <div className="animate-fade-in-up max-w-xl">
            <div className="inline-flex items-center gap-2 bg-[rgba(42,95,246,0.1)] border border-[rgba(42,95,246,0.3)] px-3 py-2 mb-6 text-xs md:text-sm text-[#2a5ff6] rounded-full">
              <Zap size={14} />
              Built on Triton SmartOS & Open Compute
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight mb-6">
              <span className="text-blue-gradient">{heroContent.tagline.split(' ').slice(0, 2).join(' ')}</span>
              <br />
              {heroContent.tagline.split(' ').slice(2).join(' ')}
            </h1>

            <p className="text-base md:text-lg lg:text-xl text-white/80 mb-8 leading-relaxed">
              {heroContent.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to="/packages" className="btn-primary justify-center sm:justify-start">
                {heroContent.primaryCTA}
                <ArrowRight size={18} />
              </Link>
              <Link to="/contact" className="btn-secondary justify-center sm:justify-start">
                {heroContent.secondaryCTA}
              </Link>
            </div>

            {/* OPT Payment Notice */}
            <div className="mt-4 flex flex-wrap items-center justify-center sm:justify-start gap-x-6 gap-y-2 text-sm text-white">
              <div className="flex items-center gap-3">
                <img 
                  src="/optio-token.png" 
                  alt="OPT Token" 
                  className="w-6 h-6 token-spin"
                />
                <span>OPT accepted here</span>
              </div>
              <div className="flex items-center gap-3">
                <img 
                  src="/sovr-token.png" 
                  alt="SOVR Token" 
                  className="w-6 h-6 token-spin"
                />
                <span>SOVR accepted here</span>
              </div>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-6 md:gap-12 mt-10 md:mt-16 pt-8 md:pt-10 border-t border-white/10">
              <div>
                <div className="text-2xl md:text-4xl font-bold text-[#2a5ff6]">99.99%</div>
                <div className="text-white/50 text-xs md:text-sm">Availability Target*</div>
              </div>
              <div>
                <div className="text-2xl md:text-4xl font-bold text-white">24/7</div>
                <div className="text-white/50 text-xs md:text-sm">Security Monitoring</div>
              </div>
            </div>
          </div>

          {/* Right - Spline 3D */}
          <div className="hidden lg:block h-[500px] xl:h-[600px] overflow-visible relative">
            <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-8 lg:px-12 bg-[var(--bg-secondary)]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-20">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold mb-4 md:mb-6">
              Why <span className="text-blue-gradient">Parler Cloud</span>?
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-white/70 max-w-2xl mx-auto">
              Open-source architecture built for high performance, efficiency, and complete transparency.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {features.map((feature, index) => {
              const icons = [Server, Shield, Zap, Globe];
              const Icon = icons[index];
              return (
                <div
                  key={feature.title}
                  className="card-dark p-6 md:p-8"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-[rgba(42,95,246,0.1)] flex items-center justify-center mb-5 md:mb-6">
                    <Icon size={24} color="#2a5ff6" />
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2 md:mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm md:text-base text-white/70">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-[var(--bg-primary)]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold mb-4 md:mb-6">
            Ready to deploy your <span className="text-blue-gradient">dream</span>?
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-white/70 mb-8 md:mb-10">
            Deploy your applications on infrastructure that's transparent, powerful, and built for scale.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link to="/packages" className="btn-primary justify-center">
              View Packages
              <ArrowRight size={18} />
            </Link>
            <Link to="/comparison" className="btn-secondary justify-center">
              Compare Platforms
            </Link>
          </div>
        </div>
      </section>

      {/* Footnote */}
      <section className="px-4 md:px-8 lg:px-12 pb-8">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-xs text-white/40 leading-relaxed">
            *Designed to support availability targets up to 99.99% depending on configuration and service tier. Targets are architectural objectives, not contractual guarantees.
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
